import { Maybe, Node, PageInfo } from '@graphql/admin/generated/graphql.types';
import { isNil, uniqBy } from 'lodash';

type RelayConnection<O> = {
  pageInfo: PageInfo;
  edges?: Maybe<{
    cursor: string;
    node: O;
  }[]>;
}

type MergeRelayEdgeOptions = {
  before?: boolean;
}

type RelayFromArrayOptions = {
  first?: number | null;
  after?: string | null;
};

export const pagination = {
  encodeCursor: (str: string) => {
    if (typeof window !== 'undefined') return btoa(str);
    return Buffer.from(str, 'utf-8').toString('base64')
  },
  nodeToCursor: (node: Node) => pagination.encodeCursor(node._id),
  mergeRelayEdge: <O extends Node, IN extends RelayConnection<O>>(
    obj: { cursor: string, node: O },
    input: IN,
    options: MergeRelayEdgeOptions = {}
  ): IN => {
    const edges = options.before
      ? [obj, ...(input.edges ?? [])]
      : [...(input.edges ?? []), obj];
    return {
      ...input,
      pageInfo: {
        ...input.pageInfo,
        totalCount: (input.pageInfo.totalCount ?? 0) + 1,
        endCursor: options.before ? input.pageInfo.endCursor : obj.cursor,
        startCursor: options.before ? obj.cursor : input.pageInfo.startCursor,
      },
      edges: uniqBy(edges, o => o.cursor),
    };
  },
  spliceRelayEdgeById: <O extends Node>(id: string, input: RelayConnection<O>): RelayConnection<O> => {
    let edgeIndex = -1;
    const cursor = pagination.encodeCursor(id);
    const inputEdges = (input.edges ?? []);
    const edges = inputEdges.filter((e, index) => {
      const match = e.cursor === cursor;
      if (match) edgeIndex = index;
      return !match;
    });

    return {
      ...input,
      pageInfo: {
        ...input.pageInfo,
        totalCount: edgeIndex > -1 ? Math.max(0, ((input.pageInfo.totalCount ?? 0) - 1)) : input.pageInfo.totalCount,
        startCursor: (edgeIndex === 0 && edges.length > 0) ? edges[0].cursor : null,
        endCursor: (edgeIndex === inputEdges.length - 1 && edges.length > 0) ? edges[edges.length - 1].cursor : null,
      },
      edges,
    };
  },
  relayFromArray: <O extends Node>(arr: O[], options: RelayFromArrayOptions = {}): RelayConnection<O> => {
    const afterIndex = !isNil(options.after) ? arr.findIndex(o => pagination.nodeToCursor(o) === options.after) : -1;
    const slice = arr.slice(afterIndex + 1, (afterIndex + 1 ) + (options.first ?? arr.length));

    const firstCursor = arr.length ? pagination.nodeToCursor(arr[0]) : null;
    const finalCursor = arr.length ? pagination.nodeToCursor(arr[arr.length - 1]) : null;
    const startCursor = slice.length ? pagination.nodeToCursor(slice[0]) : null;
    const endCursor = slice.length ? pagination.nodeToCursor(slice[slice.length - 1]) : null;

    const pageInfo: PageInfo = {
      startCursor,
      endCursor,
      pageSize: options.first ?? -1,
      totalCount: arr.length,
      hasNextPage: endCursor !== finalCursor,
      hasPreviousPage: startCursor !== firstCursor,
    };

    return {
      pageInfo,
      edges: slice.map(o => ({
        cursor: pagination.encodeCursor(o._id),
        node: o,
      }))
    }
  }
};